import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { apiGet } from "../lib/api";
import { useNavigate } from "react-router-dom";
import {
  Activity, BookOpen, Star, XCircle, TrendingUp, Crown,
  ChevronRight, ExternalLink, Brain, Sparkles,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import MotionButton from "../components/motion/MotionButton";
import MotionCard from "../components/motion/MotionCard";
import FreeTrialBanner from "../components/FreeTrialBanner";
import AcademiaTeaserBanner from "../components/AcademiaTeaserBanner";
import BookFloatingPromo from "../components/BookFloatingPromo";

const CHART_MARGIN = { left: 10, right: 16 };
const CHART_DOMAIN = [0, 100];
const CHART_TOOLTIP_STYLE = {
  background: "#FFFFFF",
  border: "1px solid #E8E4DD",
  borderRadius: 12,
  fontSize: 12,
  color: "#112A1E",
  boxShadow: "0 8px 24px -4px rgba(17,42,30,0.10)",
};
const CHART_TOOLTIP_LABEL_STYLE = { color: "#112A1E", fontWeight: 500 };
const CHART_BAR_RADIUS = [0, 8, 8, 0];
const formatPct = (v) => `${v}%`;
const BOOK_CTA_ACCURACY_THRESHOLD = 70;
const MIN_ANSWERS_FOR_BOOK_CTA = 20;
const BOOK_URL = "https://amzn.eu/d/0dWHOTMI";

const COLOR_MAP = {
  green:  { bg: "bg-brand-green/10",  text: "text-brand-green"  },
  red:    { bg: "bg-brand-red/10",    text: "text-brand-red"    },
  gold:   { bg: "bg-brand-gold/15",   text: "text-brand-gold"   },
  forest: { bg: "bg-brand-primary/8", text: "text-brand-primary"},
};

function ActionCard({ icon: Icon, title, desc, onClick, color, testId, delay }) {
  const c = COLOR_MAP[color] || COLOR_MAP.forest;
  return (
    <MotionCard
      delay={delay}
      data-testid={testId}
      onClick={onClick}
      className="cursor-pointer"
    >
      <div className={`h-11 w-11 rounded-xl ${c.bg} ${c.text} flex items-center justify-center mb-4`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif text-lg font-semibold text-brand-primary">{title}</div>
          <div className="text-xs text-brand-secondary mt-0.5">{desc}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-brand-muted" />
      </div>
    </MotionCard>
  );
}

function KPICard({ label, value, accent, delay }) {
  const accentText = accent ? COLOR_MAP[accent].text : "text-brand-primary";
  return (
    <MotionCard delay={delay} hoverLift={false} className="p-5">
      <div className="eyebrow">{label}</div>
      <div className={`font-serif text-4xl font-semibold mt-3 ${accentText} leading-none`}>{value}</div>
    </MotionCard>
  );
}

function BookCTABanner({ accuracy }) {
  return (
    <motion.a
      href={BOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="dashboard-book-cta"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.4 }}
      className="block mb-6 border border-brand-red/30 bg-gradient-to-r from-brand-red/8 via-brand-surface to-brand-green/8 rounded-2xl p-5 group transition-colors hover:border-brand-red/50"
    >
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-xl bg-brand-red/15 text-brand-red flex items-center justify-center shrink-0">
          <BookOpen className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-brand-red">// Suggerimento</div>
          <div className="text-base font-medium text-brand-primary mt-0.5">
            Sei al {accuracy}%. Per superare il 70% prova il manuale ufficiale di Medicos para Italia.
          </div>
        </div>
        <ExternalLink className="h-4 w-4 text-brand-secondary group-hover:text-brand-primary transition-colors" />
      </div>
    </motion.a>
  );
}

function SpecialtyChart({ data }) {
  return (
    <MotionCard delay={0.3} hoverLift={false} className="lg:col-span-2 p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="eyebrow">// Performance</div>
          <div className="font-serif text-2xl font-semibold text-brand-primary mt-1">
            Accuratezza per specialità
          </div>
        </div>
        <TrendingUp className="h-5 w-5 text-brand-green" />
      </div>
      <div className="h-72">
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={CHART_MARGIN}>
              <CartesianGrid stroke="#F2EFEB" horizontal={false} />
              <XAxis type="number" domain={CHART_DOMAIN} stroke="#9BA8A1" fontSize={11} />
              <YAxis dataKey="specialty" type="category" width={130} stroke="#5F7367" fontSize={11} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={formatPct}
                cursor={{ fill: "rgba(0,140,69,0.06)" }}
              />
              <Bar dataKey="pct" fill="#008C45" radius={CHART_BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-brand-secondary text-center px-6">
            Inizia a rispondere alle domande per vedere le tue statistiche per specialità.
          </div>
        )}
      </div>
    </MotionCard>
  );
}

function WeakTopics({ topics }) {
  return (
    <MotionCard delay={0.4} hoverLift={false} className="p-6">
      <div className="eyebrow">// Aree deboli</div>
      <div className="font-serif text-2xl font-semibold text-brand-primary mt-1 mb-5">
        Argomenti da rinforzare
      </div>
      <div className="space-y-2">
        {topics?.length > 0 ? (
          topics.map((t) => (
            <motion.div
              key={t.topic}
              data-testid={`weak-topic-${t.topic}`}
              whileHover={{ x: 3 }}
              className="flex items-center justify-between border border-brand-border bg-brand-surface rounded-xl px-4 py-3 transition-colors hover:border-brand-red/30"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-brand-primary truncate">{t.topic}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-secondary mt-0.5">
                  {t.total} risposte
                </div>
              </div>
              <div className="text-brand-red font-serif text-2xl font-semibold ml-3">{t.pct}%</div>
            </motion.div>
          ))
        ) : (
          <div className="text-sm text-brand-secondary leading-relaxed">
            Nessun argomento debole rilevato. Continua a esercitarti!
          </div>
        )}
      </div>
    </MotionCard>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/stats/dashboard").then(setStats).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-brand-secondary">Caricamento dashboard...</div>;
  }

  const greet = `Bentornato, ${user?.name?.split(" ")[0] || "Dottore"}`;
  const showBookCTA = stats?.total_answered >= MIN_ANSWERS_FOR_BOOK_CTA &&
                       stats?.accuracy_pct < BOOK_CTA_ACCURACY_THRESHOLD;

  return (
    <div className="px-6 sm:px-10 py-8 lg:py-12 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow">// Dashboard</div>
          <h1 className="heading-display text-4xl sm:text-5xl text-brand-primary mt-1">{greet}</h1>
          <p className="text-base text-brand-secondary mt-2">Continua dove avevi lasciato. Ogni minuto conta.</p>
        </div>
        {!user?.is_premium && (
          <MotionButton
            data-testid="dashboard-premium-cta"
            variant="red"
            onClick={() => navigate("/premium")}
          >
            <Crown className="h-4 w-4" /> Passa a Premium
          </MotionButton>
        )}
      </div>

      <FreeTrialBanner />

      {showBookCTA && <BookCTABanner accuracy={stats.accuracy_pct} />}

      <div className="eyebrow mt-2 mb-3">// Azioni rapide</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <ActionCard testId="action-training" icon={BookOpen} color="green" title="Allenamento" desc="Pratica libera" delay={0} onClick={() => navigate("/training")} />
        <ActionCard testId="action-exam" icon={Activity} color="green" title="Esame" desc="Simulazione" delay={0.05} onClick={() => navigate("/exam")} />
        <ActionCard testId="action-review" icon={Brain} color="gold" title="Ripasso SRS" desc="Anki-style" delay={0.1} onClick={() => navigate("/review")} />
        <ActionCard testId="action-mistakes" icon={XCircle} color="red" title="Errori" desc={`${stats?.mistakes_count || 0} domande`} delay={0.15} onClick={() => navigate("/mistakes")} />
      </div>

      <div className="eyebrow mb-3">// I tuoi numeri</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPICard label="Risposte totali" value={stats?.total_answered || 0} delay={0} />
        <KPICard label="Risposte corrette" value={stats?.total_correct || 0} accent="green" delay={0.05} />
        <KPICard label="Accuratezza" value={`${stats?.accuracy_pct ?? 0}%`} accent="green" delay={0.1} />
        <KPICard label="Esami completati" value={stats?.exams_completed || 0} accent="gold" delay={0.15} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <SpecialtyChart data={stats?.by_specialty} />
        <WeakTopics topics={stats?.weak_topics} />
      </div>

      <div className="mt-10 flex items-center gap-2 text-xs text-brand-secondary">
        <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
        Tip: il ripasso SRS prioritizza automaticamente i tuoi punti deboli.
      </div>

      <AcademiaTeaserBanner ctaHref="/vip" testId="dashboard-academia-teaser" />
      <BookFloatingPromo />
    </div>
  );
}
